import { Reducer, AnyAction } from 'redux';
import { isCustomerTextSet } from './utils/is-customer-text-set';
import { TextConfigurationFormState, TextConfigurationItem, TextConfigurationItemTranslation } from './types-store';
import {
  ACTION, ReceiveLanguageOptions,
  RequestText, ReceiveText, FailText,
  RequestDeleteText, ReceiveDeleteText, FailDeleteText,
  RequestSaveText, ReceiveSaveText, FailSaveText,
  ClearForm,
  UpdateTextSet, UpdateTextCollection,
  ChangeText, CopyTextToAll,
  UpdateValidation
} from './action-types';
import { clearValidationForAllParameter, clearValidationForKey, clearValidationForSingleParameter } from './reducers.helper';

export const INITIAL_STATE: TextConfigurationFormState = {
  textItem: undefined,
  originalTextItem: undefined,
  textId: undefined,
  textSet: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {}
};

const reducers: { [indexer: string]: Reducer<TextConfigurationFormState> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: TextConfigurationFormState,
    action: ReceiveLanguageOptions): TextConfigurationFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_TEXT]: (state: TextConfigurationFormState,
    action: RequestText): TextConfigurationFormState => {

    return {
      ...state,
      textItem: undefined,
      textId: action.textId,
      textSet: action.textSet,
      originalTextItem: undefined,
      fetching: true
    };
  },

  [ACTION.RECEIVE_TEXT]: (state: TextConfigurationFormState,
    action: ReceiveText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        textItem: action.textItem,
        originalTextItem: action.textItem,
        fetching: false
      }
      : state;
  },

  [ACTION.FAIL_TEXT]: (state: TextConfigurationFormState,
    action: FailText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        fetching: false
      }
      : state;
  },

  [ACTION.REQUEST_DELETE_TEXT]: (state: TextConfigurationFormState,
    action: RequestDeleteText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        deleting: true
      }
      : state;
  },

  [ACTION.RECEIVE_DELETE_TEXT]: (state: TextConfigurationFormState,
    action: ReceiveDeleteText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.FAIL_DELETE_TEXT]: (state: TextConfigurationFormState,
    action: FailDeleteText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        deleting: false
      }
      : state;
  },

  [ACTION.REQUEST_SAVE_TEXT]: (state: TextConfigurationFormState,
    action: RequestSaveText): TextConfigurationFormState => {

    return {
      ...state,
      textId: action.textId,
      textSet: action.textSet,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_TEXT]: (state: TextConfigurationFormState,
    action: ReceiveSaveText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        originalTextItem: state.textItem,
        saving: false
      }
      : state;
  },

  [ACTION.FAIL_SAVE_TEXT]: (state: TextConfigurationFormState,
    action: FailSaveText): TextConfigurationFormState => {

    return state.textId === action.textId && state.textSet === action.textSet
      ? {
        ...state,
        saving: false
      }
      : state;
  },

  [ACTION.CLEAR_FORM]: (state: TextConfigurationFormState,
    action: ClearForm): TextConfigurationFormState => {

    return {
      ...state,
      textItem: undefined,
      textId: undefined,
      textSet: undefined,
      originalTextItem: undefined,
      validation: {},
      fetching: false,
      saving: false,
      deleting: false
    };
  },

  [ACTION.UPDATE_TEXT_SET]: (state: TextConfigurationFormState,
    action: UpdateTextSet): TextConfigurationFormState => {

    const textItem = state.textItem
      ? {
        ...state.textItem,
        textSet: action.textSet
      }
      : { textSet: action.textSet } as TextConfigurationItem;
    const validation = clearValidationForKey(state.validation, 'textSet');

    // clear text collection for not development db
    const updatedTextItem = !isCustomerTextSet(textItem)
      ? textItem
      : {
        ...textItem,
        textCollection: undefined
      };
    const updatedValidation = !isCustomerTextSet(textItem)
      ? validation
      : clearValidationForKey(validation, 'textCollection');

    return {
      ...state,
      textItem: updatedTextItem,
      ...(!!updatedValidation && { validation: updatedValidation })
    };
  },

  [ACTION.UPDATE_TEXT_COLLECTION]: (state: TextConfigurationFormState,
    action: UpdateTextCollection): TextConfigurationFormState => {

    const textItem = state.textItem || {} as TextConfigurationItem;
    const validation = clearValidationForKey(state.validation, 'textCollection');

    return {
      ...state,
      textItem: {
        ...textItem,
        textCollection: action.textCollection
      },
      ...(!!validation && { validation })
    };
  },

  [ACTION.CHANGE_TEXT]: (state: TextConfigurationFormState,
    action: ChangeText): TextConfigurationFormState => {

    const textItem = state.textItem || {} as TextConfigurationItem;
    const translations = textItem.translations || [];

    return {
      ...state,
      textItem: {
        ...textItem,
        translations: translations.some(language => language.language === action.languageId)
          ? translations.map(
            language => language.language === action.languageId
              ? {
                ...language,
                text: action.text
              }
              : language
          )
          : [
            ...translations,
            {
              language: action.languageId,
              text: action.text
            } as TextConfigurationItemTranslation
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'text', action.languageId)
    };
  },

  [ACTION.COPY_TEXT_TO_ALL]: (state: TextConfigurationFormState,
    action: CopyTextToAll): TextConfigurationFormState => {

    const textItem = state.textItem || {} as TextConfigurationItem;
    const translations = textItem.translations || [];
    const targetTranslation = translations.find(language => language.language === action.languageId);

    if (targetTranslation === undefined) {
      return state;
    }

    const updatedTranslations = translations.map(
      language => ({
        ...language,
        text: targetTranslation.text
      })
    );
    const newTranslations = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !translations.some(language => language.language === languageId))
      .map(languageId => ({
        language: languageId,
        text: targetTranslation.text
      } as TextConfigurationItemTranslation));

    return {
      ...state,
      textItem: {
        ...textItem,
        translations: [
          ...updatedTranslations,
          ...newTranslations
        ]
      },
      validation: clearValidationForAllParameter(state.validation, 'text')
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: TextConfigurationFormState,
    action: UpdateValidation): TextConfigurationFormState => {

    return {
      ...state,
      validation: action.validation
    };
  }
};

export const reducer: Reducer<TextConfigurationFormState> = (
  state: TextConfigurationFormState = INITIAL_STATE, action: AnyAction) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};
